import { useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { HomeProfileSize, type DraftQuoteRequest } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Spinner } from '@fluxora/ui';

import { problemFieldsOf, problemTypeOf } from '../api/bff-client';
import { useCreateDraftQuoteMutation, useEstimateQuery } from '../api/estimate.queries';
import { FunnelBackLink } from '../components/FunnelBackLink';
import { EffectiveRateCurve } from '../components/EffectiveRateCurve';
import { EstimateBill } from '../components/EstimateBill';
import { EstimateDisclaimer } from '../components/EstimateDisclaimer';
import { QuoteDraftErrorAlert } from '../components/QuoteDraftErrorAlert';
import { StartDatePicker } from '../components/StartDatePicker';
import { useFunnelCandidateId } from '../hooks/useFunnelCandidateId';
import { useUsageSelection } from '../hooks/useUsageSelection';
import { customerSafeDetail } from '../services/bff-detail.service';
import { buildPlanStepPath, buildShelfPath } from '../services/funnel-rehydrate.service';
import { declaredMonthlyKwhFor } from '../services/usage.service';
import { useFunnel } from '../state/FunnelProvider';

/**
 * Personalization — "your estimated bill" (E-SF04).
 *
 * Reached at `/plan/:slug/estimate?candidate=<candidateId>` after the credit +
 * deposit step. It shows the estimated monthly cost for the selected plan at the
 * premise's usage (real 365-day history when available, else a home-size PROFILE
 * — AC1) with the effective-rate curve, ALWAYS behind the disclaimer (AC2 — never
 * a guaranteed price).
 *
 * ── CREATING THE DRAFT QUOTE (blocker 3) ───────────────────────────────────
 * When the customer proceeds to sign, this fires the DRAFT quote-create mutation
 * (`POST /bff/quote/draft`), which builds the `web_self_serve` quote server-side
 * from the funnel session and stores the quoteId there for the sign saga. Only on
 * success does it navigate to the sign step — it never advances on a fabricated
 * or missing quote (US fail-loud).
 *
 * All async states are handled: no candidate (idle), loading (Spinner), error
 * (Alert), and success (the estimate + a continue control).
 */
export function EstimatePage(): JSX.Element {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const funnel = useFunnel();
  const candidateId = useFunnelCandidateId();
  // The chosen usage rides every hop of the funnel (see funnel-rehydrate.service),
  // so a refresh here — or Back to the credit step — still knows it.
  const selection = useUsageSelection();
  const profileParam = searchParams.get('profile');
  const parsedProfile = profileParam === null ? undefined : HomeProfileSize.safeParse(profileParam);
  const homeProfile = parsedProfile?.success === true ? parsedProfile.data : undefined;

  // ── THE SHOPPER'S OWN kWh TRAVELS WITH THE REQUEST (E-SF19 P0a) ─────────
  // `selection.monthlyUsageKwh` is the figure the consumption step collected and
  // that every funnel link has carried since; it used to stop HERE, because the
  // only usage the request could express was a three-member home-size token. A
  // shopper declaring 120 kWh/mo was therefore priced at 500 and shown a bill
  // ~180% over, captioned "we couldn't find usage history for this address".
  //
  // `declaredMonthlyKwhFor` decides whether the selection is one the BFF will
  // accept; a figure it refuses is simply not sent, and the server falls back to
  // real history and then to the LABELLED home-size profile.
  const declaredMonthlyKwh = declaredMonthlyKwhFor(selection.monthlyUsageKwh);

  const args =
    candidateId === undefined || slug === undefined
      ? null
      : {
          candidateId,
          planId: slug,
          ...(homeProfile === undefined ? {} : { homeProfile }),
          ...(declaredMonthlyKwh === null ? {} : { declaredMonthlyKwh }),
        };

  const estimate = useEstimateQuery(args);
  const draft = useCreateDraftQuoteMutation();

  /* ── THE SUPPLY START DATE, ASKED HERE (2026-09-18) ──────────────────────
   *
   * It used to be asked on the SIGN step, one screen later, and the answer was
   * discarded: "Continue to sign" below CREATES the DRAFT quote, and from that
   * moment the quote's price and EFL are fixed and Quotation's accept path
   * never re-prices. A date chosen after this button could not reach the
   * contract without re-pricing a document mid-signature.
   *
   * Asked BEFORE the draft exists, it is simply the date the quote is built
   * from — so the contract PDF on the next screen renders the customer's date
   * because it IS the quote's `requestedStartDate`.
   *
   * The window is the server's (`estimate.data.startDate`), the same function
   * and clock the sign context uses. */
  const [startMode, setStartMode] = useState<'FIRST_AVAILABLE' | 'CHOSEN'>('FIRST_AVAILABLE');
  const [chosenDate, setChosenDate] = useState('');
  const [requestSameDay, setRequestSameDay] = useState(false);
  const [startDateError, setStartDateError] = useState<string | undefined>(undefined);

  const backLink = (
    <FunnelBackLink
      to={
        slug === undefined
          ? '/'
          : buildPlanStepPath({ tariffId: slug, step: 'credit', selection })
      }
      label={t('shop.estimate.back')}
    />
  );

  // No resolved meter → send the shopper to the landing chooser to resolve it
  // first (SHOP-6). `slug` missing is likewise a dead route → back to landing.
  if (candidateId === undefined || slug === undefined) {
    return <Navigate to="/" replace />;
  }

  /**
   * Proceed to sign.
   *
   * ── E-SF16: THE SAME CLICK MEANS TWO DIFFERENT THINGS UPSTREAM ────────────
   * For a self-serve shopper this CREATES the `web_self_serve` DRAFT quote. For a
   * customer who arrived from an agent's offer link it REPOINTS that agent's
   * existing quote (`select-plan`) — the BFF decides which, from the funnel
   * session, because only the server knows this session came from an offer. The
   * page fires one step either way, which is what keeps the funnel unchanged.
   *
   * `confirmPriceChange` is the customer's "continue at the current price" after a
   * drift refusal. It carries no token — the BFF holds the upstream's confirmation
   * server-side — so it is inert on the self-serve path and cannot be used to
   * confirm a change the server never reported.
   */
  const handleContinue = (confirmPriceChange = false): void => {
    // Resolve the date the quote will be built from. A MOVE_IN always names a
    // date; a SWITCH may take the server's first available one.
    const constraints = estimate.data?.startDate;
    const isMoveIn = constraints?.enrollmentType === 'MOVE_IN';
    const needsDate = isMoveIn || startMode === 'CHOSEN';
    let requestedStartDate: string | undefined;
    if (constraints !== undefined) {
      if (needsDate) {
        if (!/^\d{4}-\d{2}-\d{2}$/u.test(chosenDate)) {
          setStartDateError('shop.sign.startDate.error.required');
          return;
        }
        if (chosenDate < constraints.earliestDate || chosenDate > constraints.latestDate) {
          setStartDateError('shop.sign.startDate.error.range');
          return;
        }
        requestedStartDate = chosenDate;
      } else {
        requestedStartDate = constraints.firstAvailableDate;
      }
    }
    setStartDateError(undefined);

    const request: DraftQuoteRequest = {
      candidateId,
      planId: slug,
      ...(confirmPriceChange ? { confirmPriceChange: true } : {}),
      ...(requestedStartDate === undefined ? {} : { requestedStartDate }),
    };
    draft.mutate(request, {
      onSuccess: () => {
        navigate(buildPlanStepPath({ tariffId: slug, step: 'sign', selection, candidateId }));
      },
    });
  };

  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-10">
      {backLink}
      <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
        {t('shop.estimate.heading')}
      </h1>
      <p className="text-sm text-foreground/70">{t('shop.estimate.subheading')}</p>

      {estimate.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" label={t('shop.estimate.loading')} />
        </div>
      ) : null}

      {estimate.isError ? (
        <Alert variant="danger" title={t('shop.estimate.error.title')} role="alert">
          {t('shop.estimate.error.body')}
        </Alert>
      ) : null}

      {estimate.isSuccess ? (
        <>
          <EstimateBill estimate={estimate.data} />
          <EffectiveRateCurve estimate={estimate.data} />
          <EstimateDisclaimer />

          {/* Asked BEFORE "Continue to sign" creates the draft — see the note
              on the state above. The next screen's contract preview renders
              whatever is chosen here. */}
          {/* Rendered only when the BFF published a window. US-Shop and the BFF
              deploy separately, so a newer bundle can be talking to an older
              BFF that does not send one; the step then simply does not ask, and
              the BFF's next-business-day default stands. See
              `EstimateResponse.startDate`. */}
          {estimate.data.startDate === undefined ? null : (
            <StartDatePicker
              constraints={estimate.data.startDate}
              startMode={startMode}
              onStartModeChange={setStartMode}
              chosenDate={chosenDate}
              onChosenDateChange={setChosenDate}
              requestSameDay={requestSameDay}
              onRequestSameDayChange={setRequestSameDay}
              {...(startDateError === undefined ? {} : { error: startDateError })}
            />
          )}

          <Button
            variant="primary"
            shape="pill"
            onClick={() => {
              handleContinue();
            }}
            disabled={draft.isPending}
          >
            {draft.isPending
              ? t('shop.estimate.continue.creating')
              : t('shop.estimate.continue.label')}
          </Button>

          {/*
            QA-2026-08-06 — the draft-create refusal now BRANCHES. Three distinct
            upstream conditions (meter already enrolling / meter not switchable /
            ESI ID does not match the utility) used to arrive here as one alert
            telling the shopper to "try again in a moment", which could never
            work for any of them. The alert reads the problem type and offers the
            matching route forward; anything unrecognised keeps the generic copy.
          */}
          {draft.isError ? (
            <QuoteDraftErrorAlert
              problemType={problemTypeOf(draft.error)}
              serverDetail={customerSafeDetail(problemFieldsOf(draft.error))}
              onRestartMeter={() => {
                funnel.clearResolved();
                navigate('/');
              }}
              // E-SF16 — the two offer-path affordances. Both are inert on the
              // self-serve funnel, which cannot produce the refusals that render
              // them; the SERVICE decides which (if either) is offered.
              onConfirmPriceChange={() => {
                handleContinue(true);
              }}
              isConfirming={draft.isPending}
              planShelfPath={buildShelfPath(selection)}
            />
          ) : null}
        </>
      ) : null}
    </main>
  );
}
